import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import GoLiveModal from "@/components/strategy/GoLiveModal";
import { goLiveGateBlock, goLiveInitialState, submitGoLive } from "@/lib/goLive";
import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import StrategyNameButton from "@/components/strategy/StrategyNameButton";
import { openStrategyDrawer } from "@/lib/strategyDrawer";
import { matchesStrategyQuery } from "@/lib/strategyRef";
import ScheduleBuilder from "@/components/strategy/ScheduleBuilder";
import IndicatorStrategyBuilder from "@/components/strategy/IndicatorStrategyBuilder";
import LegBuilder from "@/components/strategy/LegBuilder";
import TierGate, { UsageMeter } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sparkles, Save, Play, Trash2, Loader2, Zap, History, X, Search,
  BarChart2, LayoutList, LayoutGrid, TrendingUp, TrendingDown, Minus,
  Star, Sliders, Library, Lock, Bot, ChevronDown, ChevronUp, Activity,
} from "lucide-react";
import { toast } from "sonner";

// ── Strategy Dashboard ────────────────────────────────────────────────────────

function StrategyDashboard({ items, paperPnl }) {
  const [open, setOpen] = useState(true);

  const total  = items.length;
  const live   = items.filter((s) => s.deployment?.mode === "live"  && s.deployment?.enabled).length;
  const paper  = items.filter((s) => s.deployment?.mode === "paper" && s.deployment?.enabled).length;
  const idle   = total - live - paper;

  // Gate progress: strategies close to clearing min_paper_trades
  const nearGate = items
    .filter((s) => {
      const g = s.live_gate || {};
      return g.min_paper_trades > 0 && !g.gate_passed && g.current_count > 0;
    })
    .map((s) => ({
      id: s.id,
      name: s.name,
      src: s,
      current: s.live_gate.current_count ?? 0,
      required: s.live_gate.min_paper_trades,
      pct: Math.min(100, Math.round((s.live_gate.current_count ?? 0) / s.live_gate.min_paper_trades * 100)),
    }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3);

  // Top performers by optimizer win rate
  const ranked = [...items]
    .filter((s) => s.optimizer_win_rate != null)
    .sort((a, b) => (b.optimizer_win_rate ?? 0) - (a.optimizer_win_rate ?? 0))
    .slice(0, 5);

  const pnlColor = paperPnl > 0 ? "txt-profit" : paperPnl < 0 ? "txt-loss" : "txt-secondary";

  return (
    <div className="panel overflow-hidden mb-4">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-4 py-3 text-xs font-section tracking-wider txt-secondary hover:text-[var(--text-primary)] transition-colors"
      >
        <span className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" />
          STRATEGY PORTFOLIO DASHBOARD
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="border-t border-[var(--border)] p-4 space-y-4">
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">TOTAL STRATEGIES</div>
              <div className="text-2xl font-mono-data font-bold text-[var(--text-primary)]">{total}</div>
              <div className="flex gap-2 text-[10px] font-mono-data">
                <span className="txt-muted">Idle {idle}</span>
              </div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">DEPLOYED</div>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-mono-data font-bold text-red-400">{live}</span>
                <span className="text-sm font-mono-data text-blue-400 mb-0.5">/ {paper} paper</span>
              </div>
              <div className="text-[10px] txt-muted font-section">live · paper</div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">PAPER P&L</div>
              <div className={`text-2xl font-mono-data font-bold ${pnlColor}`}>
                {paperPnl != null ? `₹${Math.abs(paperPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—"}
              </div>
              <div className="text-[10px] txt-muted font-section">cumulative</div>
            </div>
            <div className="bg-[var(--bg-surface-2)] rounded p-3 space-y-1">
              <div className="text-[10px] font-section txt-muted tracking-wider">GATE PROGRESS</div>
              <div className="text-2xl font-mono-data font-bold text-amber-400">{nearGate.length}</div>
              <div className="text-[10px] txt-muted font-section">near live gate</div>
            </div>
          </div>

          {/* Gate progress bars */}
          {nearGate.length > 0 && (
            <div>
              <div className="text-[10px] font-section txt-muted tracking-wider mb-2">LIVE GATE PROGRESS</div>
              <div className="space-y-2">
                {nearGate.map((g) => (
                  <div key={g.id}>
                    <div className="flex justify-between text-[10px] mb-1">
                      <span className="flex items-center gap-1 min-w-0 max-w-[70%]">
                        <StrategyNameButton id={g.id} name={g.name} testid={`strategy-gate-name-${g.id}`} className="txt-secondary font-medium truncate" />
                        <StrategyRefChip id={g.id} testid={`strategy-gate-ref-${g.id}`} />
                        <StrategyStatusChip src={g.src} testid={`strategy-gate-status-${g.id}`} />
                      </span>
                      <span className="font-mono-data txt-muted">{g.current}/{g.required} trades</span>
                    </div>
                    <div className="h-1.5 bg-[var(--bg-surface)] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${g.pct >= 80 ? "bg-emerald-500" : "bg-amber-500"}`}
                        style={{ width: `${g.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top performers */}
          {ranked.length > 0 && (
            <div>
              <div className="text-[10px] font-section txt-muted tracking-wider mb-2">TOP PERFORMERS · BY WIN RATE</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      {["Strategy", "Status", "Win %", "Sharpe", "P. Factor"].map((h) => (
                        <th key={h} className="text-left pb-1.5 pr-4 text-[10px] font-section txt-muted tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map((s) => {
                      const isLive  = s.deployment?.mode === "live"  && s.deployment?.enabled;
                      const isPaper = s.deployment?.mode === "paper" && s.deployment?.enabled;
                      const wr = s.optimizer_win_rate;
                      const wrColor = wr >= 60 ? "txt-profit" : wr >= 45 ? "text-amber-400" : "txt-loss";
                      return (
                        <tr key={s.id} className="border-b border-[var(--border)]/40 hover:bg-[var(--bg-surface-2)]/50">
                          <td className="py-1.5 pr-4 text-[var(--text-primary)] font-medium max-w-[220px]">
                            <div className="flex items-center gap-1 min-w-0">
                              <StrategyNameButton id={s.id} name={s.name} testid={`strategy-top-name-${s.id}`} className="truncate" />
                              <StrategyRefChip id={s.id} testid={`strategy-top-ref-${s.id}`} />
                              <StrategyStatusChip src={s} testid={`strategy-top-status-${s.id}`} />
                            </div>
                          </td>
                          <td className="py-1.5 pr-4">
                            {isLive  && <span className="text-[9px] font-section px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">LIVE</span>}
                            {isPaper && <span className="text-[9px] font-section px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">PAPER</span>}
                            {!isLive && !isPaper && <span className="text-[9px] font-section txt-muted">idle</span>}
                          </td>
                          <td className={`py-1.5 pr-4 font-mono-data font-bold ${wrColor}`}>
                            {wr != null ? `${wr.toFixed(0)}%` : "—"}
                          </td>
                          <td className="py-1.5 pr-4 font-mono-data txt-secondary">
                            {s.optimizer_sharpe != null ? s.optimizer_sharpe.toFixed(2) : "—"}
                          </td>
                          <td className="py-1.5 font-mono-data txt-secondary">
                            {s.optimizer_profit_factor != null ? s.optimizer_profit_factor.toFixed(2) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const HEALTH_MAP = {
  ok:         { label: "HEALTHY",    cls: "bg-emerald-500" },
  live_slump: { label: "LIVE SLUMP", cls: "bg-yellow-500" },
  degraded:   { label: "DEGRADED",   cls: "bg-red-500" },
};

const EXAMPLES = [
  "Buy NIFTY when 10-day SMA crosses above 30-day SMA. Exit on crossover down. 3% SL, 8% target.",
  "Mean-reversion on RELIANCE: enter long when RSI(14) < 30, exit when RSI > 60. 2% stop loss.",
  "Trend-follow BANKNIFTY using EMA 9 vs EMA 21. Stop loss 2.5%, target 7%.",
  "Momentum strategy on ICICIBANK: buy when EMA 5 crosses above EMA 20, exit on reverse. 2% SL.",
  "Trend-follow CRUDEOIL using SMA 10 vs SMA 30 crossover. Stop loss 3%, target 9%.",
  "Mean-reversion on TATAMOTORS: buy when RSI(14) < 35, exit when RSI > 65. 2.5% stop loss.",
  "Breakout strategy on SBIN: enter when close crosses above 20-day SMA, exit on crossover down. 2% SL.",
  "Trend-follow GOLD using EMA 9 vs EMA 21. Stop loss 1.5%, target 5%.",
  "Bull Call Spread on NIFTY: buy ATM call and sell OTM+1 call when RSI is above 55. Weekly expiry. Stop at 50% of premium.",
  "Short Strangle on BANKNIFTY: sell ATM call and ATM put when ADX < 20 (low volatility). Exit at 50% profit or if underlying moves 1% against.",
  "Iron Condor on NIFTY: sell OTM1 CE and OTM1 PE, buy OTM2 CE and OTM2 PE when VIX is low. Target 50% of max credit.",
  "Buy NIFTY straddle (ATM call + ATM put) before major events. Exit when either leg doubles. Stop at 30% of premium.",
  "Put Buy + Call Sell on BANKNIFTY when EMA 9 crosses below EMA 21. Bearish synthetic. Weekly expiry.",
];

const fmtN = (v, d=2) => (v==null||isNaN(v)) ? "—" : Number(v).toLocaleString("en-IN",{minimumFractionDigits:d,maximumFractionDigits:d});

// ── Strategy card & library components ───────────────────────────────────────

function scoreColor(val, thresholds) {
  if (val == null) return "txt-muted";
  if (val >= thresholds[1]) return "txt-profit";
  if (val >= thresholds[0]) return "text-amber-400";
  return "txt-loss";
}

function ScoreCell({ label, value, fmt = (v) => v, thresholds = [0, 0] }) {
  const cls = scoreColor(value, thresholds);
  return (
    <div className="text-center">
      <div className={`font-mono-data text-base font-bold ${cls}`}>
        {value != null ? fmt(value) : <span className="txt-muted text-sm">—</span>}
      </div>
      <div className="overline text-[9px] txt-muted mt-0.5">{label}</div>
    </div>
  );
}

function StarRating({ score }) {
  if (score == null) return <span className="text-[10px] txt-muted">—</span>;
  const stars = Math.max(1, Math.round(score / 20));
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i <= stars ? "text-amber-400 fill-amber-400" : "txt-muted"}`} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function StrategyCard({ s, onDeploy, onLive, onVersions, onPerf, onBacktest, onDelete, onGate }) {
  const isLive  = s.deployment?.mode === "live"  && s.deployment?.enabled;
  const isPaper = s.deployment?.mode === "paper" && s.deployment?.enabled;
  const health  = HEALTH_MAP[s.optimizer_health] || HEALTH_MAP.ok;
  const gate = s.live_gate || {};
  const gateBlocked = gate.min_paper_trades > 0 && !gate.gate_passed;

  const winRate = s.optimizer_win_rate;
  const sharpe  = s.optimizer_sharpe;
  const dd      = s.optimizer_max_drawdown;
  const pf      = s.optimizer_profit_factor;
  const score = winRate != null ? Math.min(100, Math.round(
    (winRate * 0.4) + (Math.min(sharpe ?? 0, 3) / 3 * 30) +
    (Math.min(pf ?? 0, 3) / 3 * 20) + (Math.max(0, 1 - (dd ?? 30) / 30) * 10)
  )) : null;

  return (
    <div className="panel p-4 flex flex-col gap-3 border border-[var(--border)] hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-section text-sm truncate"><StrategyNameButton id={s.id} name={s.name} testid={`strategy-card-name-${s.id}`} /></div>
          <div className="mt-0.5 flex items-center gap-1 flex-wrap">
            <StrategyRefChip id={s.id} testid={`strategy-card-ref-${s.id}`} />
            <StrategyStatusChip src={s} testid={`strategy-card-status-${s.id}`} />
          </div>
          <div className="text-xs txt-muted mt-0.5">
            <span className="font-mono-data">{s.dsl?.symbol || "—"}</span>
            <span className="mx-1 txt-muted">·</span>
            <span className="txt-muted">{s.dsl?.entry_timeframe || s.dsl?.timeframe || "1d"}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {s.rank_label && (
            <span className={`text-[10px] font-section px-1.5 py-0.5 border ${
              s.rank_label === "S" ? "bg-amber-500/20 text-amber-300 border-amber-500/40" :
              s.rank_label === "A" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
              s.rank_label === "B" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" :
              "border-[var(--border)] txt-muted"
            }`} title={`Rank score: ${s.rank_score}/100`}>
              {s.rank_label}
            </span>
          )}
          {s.dsl?.legs?.length > 0 && (
            <span className="text-[10px] font-section px-1.5 py-0.5 bg-sky-500/20 text-sky-300 border border-sky-500/30" title={`${s.dsl.legs.length}-leg options spread`}>
              {s.dsl.legs.length}L
            </span>
          )}
          {isLive  && <span className="text-[10px] font-section px-1.5 py-0.5 bg-red-500/20 text-red-300 border border-red-500/30">LIVE</span>}
          {isLive && s.deployment?.autopilot && <span className="text-[10px] font-section px-1.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30">AUTO</span>}
          {isPaper && <span className="text-[10px] font-section px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">PAPER</span>}
          {!isLive && !isPaper && <span className="text-[10px] txt-muted">PAUSED</span>}
        </div>
      </div>

      {score != null && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] txt-muted font-section">SCORE</span>
            <span className={`text-xs font-mono-data font-bold ${score >= 65 ? "txt-profit" : score >= 40 ? "text-amber-400" : "txt-loss"}`}>{score}/100</span>
          </div>
          <div className="h-1 bg-[var(--bg-page)] rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${score >= 65 ? "bg-emerald-500" : score >= 40 ? "bg-amber-400" : "bg-red-500"}`} style={{width:`${score}%`}} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-2 border border-[var(--border)] p-2 bg-[var(--bg-page)]">
        <ScoreCell label="WIN %" value={winRate} fmt={(v) => `${v.toFixed(0)}%`} thresholds={[45, 55]} />
        <ScoreCell label="SHARPE" value={sharpe} fmt={(v) => v.toFixed(2)} thresholds={[0.5, 1.0]} />
        <ScoreCell label="MAX DD" value={dd} fmt={(v) => `${v.toFixed(1)}%`} thresholds={[-20, -10]} />
        <ScoreCell label="PF" value={pf} fmt={(v) => v.toFixed(2)} thresholds={[1.0, 1.5]} />
      </div>

      <div className="grid grid-cols-4 gap-2 text-center border border-[var(--border)] bg-[var(--bg-page)] p-2">
        <div>
          <div className="text-[10px] txt-muted font-section">PAPER</div>
          <div className="font-mono-data text-sm font-bold">{s.trade_count ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] txt-muted font-section">WIN %</div>
          <div className={`font-mono-data text-sm font-bold ${(s.paper_win_rate??0)>=55?"txt-profit":(s.paper_win_rate??0)>=45?"text-amber-400":"txt-muted"}`}>
            {s.trade_count > 0 ? `${(s.paper_win_rate??0).toFixed(0)}%` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] txt-muted font-section">LIVE</div>
          <div className="font-mono-data text-sm font-bold">{s.live_order_count ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] txt-muted font-section">P&amp;L</div>
          <div className={`font-mono-data text-sm font-bold ${(s.paper_pnl??0)>0?"txt-profit":(s.paper_pnl??0)<0?"txt-loss":"txt-muted"}`}>
            {s.paper_pnl!=null&&s.paper_pnl!==0?`${s.paper_pnl>0?"+":""}₹${Math.abs(s.paper_pnl).toLocaleString("en-IN",{maximumFractionDigits:0})}`:"—"}
          </div>
        </div>
      </div>

      {/* Live gate indicator */}
      {gate.min_paper_trades > 0 && (
        <div className={`flex items-center gap-2 px-2 py-1.5 border text-[10px] font-section ${
          gate.gate_passed ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400" : "border-amber-500/30 bg-amber-500/5 text-amber-400"
        }`}>
          {gate.gate_passed ? <Zap className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
          <span>{gate.gate_passed ? "LIVE GATE PASSED" : `GATE: ${gate.current_count ?? 0}/${gate.min_paper_trades} PAPER TRADES`}</span>
          <button onClick={() => onGate(s)} className="ml-auto txt-muted hover:text-white">EDIT</button>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs">
        <StarRating score={score} />
        <span className={`w-1.5 h-1.5 rounded-full ml-2 ${health.cls}`} />
        <span className="txt-secondary text-[10px] font-section">{health.label}</span>
        {s.deployment?.broker && <span className="ml-auto font-mono-data text-[10px] txt-muted">{s.deployment.broker}</span>}
      </div>

      <div className="flex items-center gap-1 border-t border-[var(--border)] pt-2">
        <Button size="sm" variant="ghost" onClick={() => onDeploy(s)}
          className={`rounded-none h-7 px-2 text-xs font-section flex-1 ${isLive?"text-red-400":isPaper?"txt-profit":"txt-muted"}`}
          title={isLive?"Stop live":isPaper?"Pause":"Run paper"}>
          <Sparkles className="w-3 h-3 mr-1" />
          {isLive?"STOP":isPaper?"PAUSE":"PAPER"}
        </Button>
        {!isLive && (
          <Button size="sm" variant="ghost" onClick={() => onLive(s)}
            className={`rounded-none h-7 px-2 ${gateBlocked?"txt-muted opacity-40 cursor-not-allowed":"text-red-400 hover:bg-red-500/10"}`}
            title={gateBlocked ? `Live locked: needs ${gate.min_paper_trades} paper trades` : "Go live"}>
            <Zap className="w-3 h-3" />
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onGate(s)} className="rounded-none h-7 px-2 txt-muted hover:text-amber-400" title="Live gate settings"><Lock className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onVersions(s)} className="rounded-none h-7 px-2" title="Versions"><History className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onPerf(s)}     className="rounded-none h-7 px-2" title="Performance"><BarChart2 className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onBacktest(s)} className="rounded-none h-7 px-2" title="Backtest"><Play className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)} className="rounded-none h-7 px-2 hover:text-red-400" title="Delete"><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

function BacktestInlineResult({ result }) {
  if (!result) return null;
  const ret = result.total_return_pct ?? 0;
  const retPos = ret > 0;
  return (
    <div className="border border-[var(--border)] bg-[var(--bg-page)] p-4 space-y-3">
      <div className="flex items-center gap-2">
        {retPos ? <TrendingUp className="w-4 h-4 txt-profit" /> : <TrendingDown className="w-4 h-4 txt-loss" />}
        <span className="overline text-xs">Pre-save backtest · 180 days · ₹5L capital</span>
        <span className="text-xs txt-muted ml-auto">Save strategy to persist these results</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
        {[
          {label:"Return",  val:`${ret>=0?"+":""}${fmtN(ret)}%`,              cls:retPos?"txt-profit":"txt-loss"},
          {label:"Win %",   val:`${fmtN(result.win_rate_pct)}%`,               cls:(result.win_rate_pct??0)>=50?"txt-profit":"txt-loss"},
          {label:"Sharpe",  val:fmtN(result.sharpe),                           cls:(result.sharpe??0)>=1?"txt-profit":"txt-muted"},
          {label:"Max DD",  val:`${fmtN(result.max_drawdown_pct)}%`,           cls:"txt-loss"},
          {label:"Trades",  val:result.total_trades ?? "—",                    cls:""},
        ].map(m=>(
          <div key={m.label}>
            <div className={`font-mono-data font-bold text-sm ${m.cls}`}>{m.val}</div>
            <div className="overline text-[9px] txt-muted mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Library panel (shared between tab and table view) ─────────────────────────

// RU-STRATLINK SL-8: name / symbol substring or id prefix, the status filter, and "has an open paper position".
const STRATEGY_FIELDS = { name: (s) => s.name, symbol: (s) => s.dsl?.symbol, id: (s) => s.id };
export function filterStrategies(items, { search = "", filter = "all", openOnly = false, openIds = new Set() } = {}) {
  // SL-12(d) / RU-REFSEARCH RS-1: the shared matcher ("ref a1b2c3d4" matches like the bare ref)
  return items.filter((s) => {
    const matchSearch = matchesStrategyQuery(s, search, STRATEGY_FIELDS);
    const depMode = s.deployment?.mode;
    const depOn   = s.deployment?.enabled;
    const matchFilter =
      filter==="all" ? true :
      filter==="live"  ? (depMode==="live"  && depOn) :
      filter==="paper" ? (depMode==="paper" && depOn) :
      !depOn;
    return matchSearch && matchFilter && (!openOnly || openIds.has(String(s.id)));
  });
}

// RU-STRATLINK SL-8 (G7 #3): every page of GET /strategies (the API serves at most 200 per page), so the
// search, the open-position filter and a deep link cover the whole library. Returns {items, complete, error}:
// a failed later page keeps the pages already loaded (complete false); a failed first page throws.
export const STRATEGY_PAGE = 200;
export async function fetchAllStrategies(get, maxPages = 100) {
  const items = [];
  const seen = new Set();     // SL-8: a page shifted by a concurrent insert never lists a strategy twice
  let served = 0;
  for (let page = 0; page < maxPages; page += 1) {
    let data;
    try {
      ({ data } = await get("/strategies", { params: { limit: STRATEGY_PAGE, skip: page * STRATEGY_PAGE } }));
    } catch (error) {
      if (page === 0) throw error;
      return { items, complete: false, error };
    }
    const got = data?.items || [];
    served += got.length;
    for (const s of got) {
      const key = String(s?.id);
      if (!seen.has(key)) { seen.add(key); items.push(s); }
    }
    const total = Number(data?.total);
    if (got.length < STRATEGY_PAGE || (Number.isFinite(total) && served >= total)) {
      return { items, complete: true };
    }
  }
  return { items, complete: false };
}

// RU-STRATLINK SL-5 (G7 #2): why a linked strategy is not in the library, from its position row (if any).
export function linkNotFoundText(linkedId, row, listComplete) {
  const ref = String(linkedId).slice(0, 8);
  if (!listComplete) {
    return `Strategy ${ref} was not found in the part of the library that loaded (the list did not load completely). Reload to try again.`;
  }
  if (row && row.strategy_pausable) {
    return `Strategy ${ref} is not in the library. Pause or resume its paper trading from the Trade screen.`;
  }
  if (row && row.strategy_mode === "live") {
    return `Strategy ${ref} is a live strategy that is not in the library (a live signal-execution record); the Trade screen never pauses a live strategy.`;
  }
  if (row && row.strategy_mode === "paper") {
    return `Strategy ${ref} is a signal-execution record, not a library strategy: it cannot be paused; its positions follow their own stop / target orders.`;
  }
  return `Strategy ${ref} is not in your library (deleted, or not one of your strategies).`;
}

// Strategy ids holding an open paper position (GET /paper/positions rows with qty != 0).
export const openPaperStrategyIds = (positions) => new Set((positions || [])
  .filter((p) => p.strategy_id && Number(p.qty) !== 0).map((p) => String(p.strategy_id)));

function LibraryContent({ items, stratSearch, setStratSearch, stratFilter, setStratFilter, stratLayout, setStratLayout,
  onDeploy, onLive, onVersions, onPerf, onBacktest, onDelete, onGate,
  openOnly = false, setOpenOnly = () => {}, openIds = new Set(), linkedId = null, linkedRow = null,
  listComplete = true }) {
  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-3 py-3 border-b border-[var(--border)] flex-wrap gap-y-2">
        <div className="overline txt-muted">
          {items.length} strateg{items.length===1?"y":"ies"}
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 txt-muted" />
          <Input value={stratSearch} onChange={(e) => setStratSearch(e.target.value)}
            placeholder="Search by name, symbol or ref / id…" data-testid="strategy-search"
            className="pl-8 rounded-none h-8 text-sm bg-[var(--bg-page)] border-[var(--border)]" />
        </div>
        <div className="flex gap-1">
          {["all", "live", "paper", "paused"].map((f) => (
            <button key={f} onClick={() => setStratFilter(f)}
              className={`text-xs px-3 py-1 font-section border transition-colors ${stratFilter===f?"border-white text-white bg-[var(--bg-surface-2)]":"border-[var(--border)] txt-muted hover:border-white/40"}`}>
              {f.toUpperCase()}
            </button>
          ))}
          <button type="button" onClick={() => setOpenOnly(!openOnly)} aria-pressed={openOnly}
            data-testid="strategy-filter-open-position" title="Only strategies with an open paper position"
            className={`text-xs px-3 py-1 font-section border transition-colors ${openOnly?"border-white text-white bg-[var(--bg-surface-2)]":"border-[var(--border)] txt-muted hover:border-white/40"}`}>
            OPEN POSITION
          </button>
        </div>
        <div className="ml-auto flex gap-1 border border-[var(--border)]">
          <button onClick={() => setStratLayout("cards")}
            className={`p-1.5 transition-colors ${stratLayout==="cards"?"bg-[var(--bg-surface-2)] text-white":"txt-muted hover:text-white"}`} title="Card view">
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setStratLayout("table")}
            className={`p-1.5 transition-colors ${stratLayout==="table"?"bg-[var(--bg-surface-2)] text-white":"txt-muted hover:text-white"}`} title="Table view">
            <LayoutList className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {items.length === 0 ? (
        <div className="py-12 text-center txt-muted text-sm">No strategies yet. Build one using the AI or Indicator builder above.</div>
      ) : (() => {
        const filtered = filterStrategies(items, { search: stratSearch, filter: stratFilter, openOnly, openIds });

        if (filtered.length === 0) {
          // RU-STRATLINK SL-5: a strategy linked from the Trade screen that the library does not list.
          if (linkedId && stratSearch === linkedId) {
            return (
              <div className="py-8 text-center txt-muted text-sm" data-testid="strategy-link-not-found">
                {linkNotFoundText(linkedId, linkedRow, listComplete)}
              </div>
            );
          }
          return <div className="py-8 text-center txt-muted text-sm">No strategies match filter.</div>;
        }

        if (stratLayout === "cards") {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pt-4">
              {filtered.map(s=>(
                <StrategyCard key={s.id} s={s}
                  onDeploy={onDeploy} onLive={onLive} onVersions={onVersions}
                  onPerf={onPerf} onBacktest={onBacktest} onDelete={onDelete}
                  onGate={onGate} />
              ))}
            </div>
          );
        }

        return (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--border)]">
                <TableHead className="overline">Strategy</TableHead>
                <TableHead className="overline">Symbol</TableHead>
                <TableHead className="overline">TF</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline">Broker</TableHead>
                <TableHead className="overline text-right">Win %</TableHead>
                <TableHead className="overline text-right">Sharpe</TableHead>
                <TableHead className="overline">Health</TableHead>
                <TableHead className="overline">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const isLive  = s.deployment?.mode==="live"  && s.deployment?.enabled;
                const isPaper = s.deployment?.mode==="paper" && s.deployment?.enabled;
                const health  = HEALTH_MAP[s.optimizer_health] || HEALTH_MAP.ok;
                return (
                  <TableRow key={s.id} className="border-[var(--border)]" data-testid={`strategy-item-${s.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StrategyNameButton id={s.id} name={s.name} testid={`strategy-row-name-${s.id}`} className="font-section text-sm" />
                        <StrategyRefChip id={s.id} testid={`strategy-row-ref-${s.id}`} />
                        <StrategyStatusChip src={s} testid={`strategy-row-status-${s.id}`} />
                      </div>
                      {s.description && <div className="text-xs txt-secondary mt-0.5 max-w-xs truncate">{s.description}</div>}
                    </TableCell>
                    <TableCell className="font-mono-data text-xs">{s.dsl?.symbol||"—"}</TableCell>
                    <TableCell className="font-mono-data text-xs txt-muted">{s.dsl?.entry_timeframe||s.dsl?.timeframe||"1d"}</TableCell>
                    <TableCell>
                      {isLive  && <span className="text-xs font-section px-2 py-0.5 bg-red-500/20 text-red-300 border border-red-500/30">LIVE</span>}
                      {isPaper && <span className="text-xs font-section px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">PAPER</span>}
                      {!isLive && !isPaper && <span className="text-xs txt-muted">Paused</span>}
                    </TableCell>
                    <TableCell className="text-xs txt-muted">{s.deployment?.broker||"—"}</TableCell>
                    <TableCell className="font-mono-data text-right text-sm">
                      {s.optimizer_win_rate!=null
                        ? <span className={s.optimizer_win_rate>=50?"txt-profit":"txt-loss"}>{s.optimizer_win_rate.toFixed(1)}%</span>
                        : <span className="txt-muted">—</span>}
                    </TableCell>
                    <TableCell className="font-mono-data text-right text-sm">
                      {s.optimizer_sharpe!=null
                        ? <span className={s.optimizer_sharpe>=1?"txt-profit":"txt-muted"}>{s.optimizer_sharpe.toFixed(2)}</span>
                        : <span className="txt-muted">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${health.cls}`} />
                        <span className="text-xs txt-secondary">{health.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={()=>onDeploy(s)}
                          className={`rounded-none h-7 px-2 text-xs font-section ${isLive?"text-red-400":isPaper?"txt-profit":"txt-muted"}`}
                          title={isLive?"LIVE — stop":isPaper?"PAPER — pause":"Deploy paper"}>
                          <Sparkles className="w-3 h-3 mr-1" />
                          {isLive?"STOP":isPaper?"PAUSE":"PAPER"}
                        </Button>
                        {!isLive && (
                          <Button size="sm" variant="ghost" onClick={()=>onLive(s)}
                            className="rounded-none h-7 px-2 text-red-400 hover:bg-red-500/10" title="Go live">
                            <Zap className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={()=>onVersions(s)} className="rounded-none h-7 px-2" title="Version history"><History className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={()=>onPerf(s)}     className="rounded-none h-7 px-2" title="Trade performance"><BarChart2 className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={()=>onBacktest(s)} className="rounded-none h-7 px-2" title="Run backtest"><Play className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={()=>onDelete(s.id)} className="rounded-none h-7 px-2 hover:text-red-400" title="Delete"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        );
      })()}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function StrategyBuilder() {
  const navigate = useNavigate();

  // Builder state
  const [builderMode,    setBuilderMode]    = useState("ai"); // "ai" | "indicator" | "library"
  const [prompt,         setPrompt]         = useState(EXAMPLES[0]);
  const [generating,     setGenerating]     = useState(false);
  const [dsl,            setDsl]            = useState(null);
  const [dslText,        setDslText]        = useState("");
  const [dslError,       setDslError]       = useState("");
  const [schedule,       setSchedule]       = useState(null);
  const [name,           setName]           = useState("");
  const [tfMode,         setTfMode]         = useState("auto"); // "auto" | "manual"
  const [entryTimeframe, setEntryTimeframe] = useState("5");
  const [exitTimeframe,  setExitTimeframe]  = useState("1");
  const [btLoading,      setBtLoading]      = useState(false);
  const [btResult,       setBtResult]       = useState(null);

  // Library state
  const [items,          setItems]          = useState([]);
  const [stratSearch,    setStratSearch]    = useState("");
  const [stratFilter,    setStratFilter]    = useState("all");
  const [stratLayout,    setStratLayout]    = useState("cards");

  // Modals / drawers
  const [liveModal,      setLiveModal]      = useState(null);
  const [liveSubmitting, setLiveSubmitting] = useState(false);
  const [versionsPanel,  setVersionsPanel]  = useState(null);
  const [versionsLoading,setVersionsLoading]= useState(false);
  const [gateModal,      setGateModal]      = useState(null); // { strategyId, name, current }
  const [autopilotCandidates, setAutopilotCandidates] = useState([]);
  const [adoptingId,     setAdoptingId]     = useState(null);
  const [paperPnl,       setPaperPnl]       = useState(null);
  // RU-STRATLINK SL-5 / SL-8: deep link `?strategy=<id>`, the "has an open paper position" filter.
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("strategy");
  const [openOnly,       setOpenOnly]       = useState(false);
  const [openIds,        setOpenIds]        = useState(() => new Set());
  const [posRows,        setPosRows]        = useState([]);
  const [listComplete,   setListComplete]   = useState(true);

  const fetchOpenPositions = async () => {
    try {
      const { data } = await api.get("/paper/positions");
      setOpenIds(openPaperStrategyIds(data?.positions));
      setPosRows(data?.positions || []);
    } catch { setOpenIds(new Set()); setPosRows([]); }
  };

  const fetchList = async () => {
    try {
      const { items: all, complete } = await fetchAllStrategies(api.get);
      setItems(all);
      setListComplete(complete);
      if (!complete) toast.error("Strategy list did not load completely");
    } catch (e) {
      setListComplete(false);
      toast.error(e?.response?.data?.detail || "Failed to load strategies");
    }
  };

  const fetchPaperPnl = async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setPaperPnl(data.paper?.total_pnl ?? data.paper?.pnl ?? null);
    } catch { /* non-critical */ }
  };

  const fetchAutopilotCandidates = async () => {
    try {
      const { data } = await api.get("/strategies/autopilot/candidates");
      setAutopilotCandidates(data.items || []);
    } catch { /* not yet built — ok */ }
  };

  /* eslint-disable */
  useEffect(() => { fetchList(); fetchAutopilotCandidates(); fetchPaperPnl(); fetchOpenPositions(); }, []);
  useEffect(() => {
    if (linkedId) { setBuilderMode("library"); setStratSearch(linkedId); setStratFilter("all"); }
  }, [linkedId]);
  /* eslint-enable */

  // ── AI Builder ──────────────────────────────────────────────────────────────

  const generate = async () => {
    setGenerating(true);
    setBtResult(null);
    try {
      const payload = { prompt };
      if (tfMode === "manual") payload.timeframe = entryTimeframe;
      const { data } = await api.post("/strategies/generate", payload);
      // In auto mode, sync TF selectors to what the AI chose
      if (tfMode === "auto" && data.dsl?.timeframe) {
        setEntryTimeframe(data.dsl.timeframe);
        setExitTimeframe(data.dsl.timeframe);
      }
      // If AI extracted a schedule (e.g. from time-window prompt), pre-fill the ScheduleBuilder
      if (data.dsl?.schedule) setSchedule(data.dsl.schedule);
      setDsl(data.dsl);
      setDslText(JSON.stringify(data.dsl, null, 2));
      setDslError("");
      setName(data.dsl?.name || "Untitled");
      toast.success("Strategy generated by AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onDslTextChange = (text) => {
    setDslText(text);
    try {
      const parsed = JSON.parse(text);
      setDsl(parsed);
      setDslError("");
    } catch {
      setDslError("Invalid JSON — fix before saving");
    }
  };

  const buildFinalDsl = () => ({
    ...dsl,
    ...(schedule ? { schedule } : { schedule: undefined }),
    entry_timeframe: entryTimeframe,
    exit_timeframe:  exitTimeframe,
  });

  const runPreviewBacktest = async () => {
    if (!dsl) return;
    if (dslError) { toast.error("Fix DSL errors before backtesting"); return; }
    setBtLoading(true);
    setBtResult(null);
    try {
      const { data } = await api.post("/backtest/run", {
        dsl: buildFinalDsl(),
        capital: 500000,
        slippage_bps: 5,
        fee_bps: 2,
        days: 180,
        save: false, // don't persist — strategy isn't saved yet
      });
      setBtResult(data);
      toast.success("Backtest complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backtest failed");
    } finally {
      setBtLoading(false);
    }
  };

  const save = async () => {
    if (!dsl) return;
    if (dslError) { toast.error(dslError); return; }
    try {
      await api.post("/strategies", { name, description: dsl.description, dsl: buildFinalDsl() });
      toast.success("Strategy saved");
      setDsl(null);
      setDslText("");
      setDslError("");
      setName("");
      setSchedule(null);
      setBtResult(null);
      fetchList();
      setBuilderMode("library"); // jump to library to see the saved strategy
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // ── Library actions ─────────────────────────────────────────────────────────

  const remove = async (id) => {
    try {
      await api.delete(`/strategies/${id}`);
      toast.success("Deleted");
      fetchList();
    } catch { toast.error("Delete failed"); }
  };

  const toggleDeploy = async (s) => {
    if (s.deployment?.mode === "live" && s.deployment?.enabled) {
      try {
        await api.post(`/strategies/${s.id}/deploy`, { mode: "paper", enabled: false });
        toast.success(`${s.name} live deployment STOPPED`);
        fetchList();
      } catch (e) { toast.error(e?.response?.data?.detail || "Stop failed"); }
      return;
    }
    const next = !s.deployment?.enabled;
    try {
      await api.post(`/strategies/${s.id}/deploy`, { mode: "paper", enabled: next });
      toast[next?"warning":"success"](next ? `${s.name} is now running in PAPER mode` : `${s.name} paused`);
      fetchList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deploy toggle failed"); }
  };

  const openLiveModal = (s) => {
    const blocked = goLiveGateBlock(s);    // RU-SUITEOPS SO-7: shared with the Strategy Suites review
    if (blocked) {
      toast.error(blocked);
      return;
    }
    setLiveModal(goLiveInitialState(s));
  };

  const saveGate = async () => {
    if (!gateModal) return;
    try {
      await api.patch(`/strategies/${gateModal.strategyId}/live-gate`, { min_paper_trades: gateModal.value });
      toast.success("Live gate updated");
      setGateModal(null);
      fetchList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const adoptCandidate = async (candidateId, name) => {
    setAdoptingId(candidateId);
    try {
      await api.post(`/strategies/autopilot/adopt/${candidateId}`);
      toast.success(`"${name}" added to your strategy library`);
      fetchList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adopt failed"); }
    finally { setAdoptingId(null); }
  };

  const submitLiveDeploy = async () => {
    if (!liveModal) return;
    if (liveModal.typed !== "DEPLOY LIVE") { toast.error("Type 'DEPLOY LIVE' exactly to confirm"); return; }
    setLiveSubmitting(true);
    try {
      await submitGoLive(api, liveModal);
      toast.warning(`${liveModal.strategy.name} is LIVE on ${liveModal.broker.toUpperCase()}`);
      setLiveModal(null);
      fetchList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Live deploy failed"); }
    finally { setLiveSubmitting(false); }
  };

  const openVersions = async (s) => {
    setVersionsLoading(true);
    try {
      const { data } = await api.get(`/strategies/${s.id}/versions`);
      setVersionsPanel({ strategyId: s.id, name: s.name, versions: data.versions });
    } catch { toast.error("Could not load version history"); }
    finally { setVersionsLoading(false); }
  };

  const restoreVersion = async (strategyId, v) => {
    if (!window.confirm(`Restore "${versionsPanel.name}" to v${v.version}? Current DSL will be archived.`)) return;
    try {
      await api.put(`/strategies/${strategyId}`, { name: v.name, dsl: v.dsl });
      toast.success(`Restored to v${v.version}`);
      setVersionsPanel(null);
      fetchList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const runBacktest = (s) => {
    // Navigate to Backtest page with strategy pre-selected
    sessionStorage.setItem("af_backtest_dsl", JSON.stringify({ dsl: s.dsl, id: s.id, name: s.name }));
    navigate("/backtest");
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const TABS = [
    { key:"ai",        label:"AI Builder",        icon:<Sparkles className="w-3.5 h-3.5" /> },
    { key:"indicator", label:"Indicator Builder",  icon:<Sliders  className="w-3.5 h-3.5" /> },
    { key:"library",   label:`Strategy Library (${items.length})`, icon:<Library  className="w-3.5 h-3.5" /> },
  ];

  return (
    <AppShell>
      <PageHeader
        overline="AI / Quant Engine"
        title="Strategy Builder"
        description="Build strategies with AI or visual indicators. Backtest before saving, then deploy to paper or live trading."
      />

      <div className="p-8">
        <StrategyDashboard items={items} paperPnl={paperPnl} />
        <div className="panel" data-testid="strategy-builder-panel">
          {/* Tab bar */}
          <div className="flex gap-0 border-b border-[var(--border)] px-6">
            {TABS.map(tab=>(
              <button key={tab.key} type="button" onClick={()=>setBuilderMode(tab.key)}
                className={`flex items-center gap-1.5 text-xs font-section tracking-wider px-4 py-3 border-b-2 transition-colors ${builderMode===tab.key?"border-white text-white":"border-transparent txt-muted hover:text-[var(--text-primary)]"}`}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>

          {/* ── AI Builder ── */}
          {builderMode === "ai" && (
            <div className="p-6 space-y-5">
              <TierGate
                required="pro"
                feature="AI Strategy Builder"
                description="Describe your strategy in plain English — AI converts it to a live-tradeable DSL in seconds."
              >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="overline">Natural-language strategy</Label>
                  <UsageMeter resource="strategies" label="strategies" />
                </div>
                <Textarea
                  data-testid="strategy-prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="mt-2 min-h-[110px] rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-sm"
                  placeholder="Describe entry, exit, stops…"
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {EXAMPLES.map((ex, i) => (
                    <button key={`ex-${i}`} data-testid={`strategy-example-${i}`}
                      onClick={() => setPrompt(ex)}
                      className="text-xs px-2 py-1 border border-[var(--border)] hover:border-white txt-secondary hover:text-white transition-colors">
                      Example {i + 1}
                    </button>
                  ))}
                </div>
              </div>

              {/* Multi-timeframe */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="overline">Candle timeframe</Label>
                  {/* Auto / Manual toggle */}
                  <div className="flex items-center gap-1 border border-[var(--border)] p-0.5">
                    {["auto","manual"].map((mode) => (
                      <button key={mode} type="button" onClick={() => setTfMode(mode)}
                        className={`text-[10px] px-3 py-1 font-section tracking-wider transition-colors ${tfMode===mode?"bg-white text-black":"txt-muted hover:text-white"}`}>
                        {mode === "auto" ? "AI AUTO" : "MANUAL"}
                      </button>
                    ))}
                  </div>
                  {tfMode === "auto" && (
                    <span className="text-[10px] txt-muted">AI picks the best timeframe from your prompt</span>
                  )}
                </div>
                {tfMode === "manual" && [
                  { label: "Entry TF", value: entryTimeframe, set: setEntryTimeframe },
                  { label: "Exit TF",  value: exitTimeframe,  set: setExitTimeframe  },
                ].map(({ label, value, set }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs txt-secondary w-14 font-section">{label}</span>
                    <div className="flex flex-wrap gap-1">
                      {[
                        {value:"1",label:"1 min"},{value:"3",label:"3 min"},{value:"5",label:"5 min"},
                        {value:"15",label:"15 min"},{value:"60",label:"1 hour"},{value:"1d",label:"Daily"},
                      ].map((tf) => (
                        <button key={tf.value} type="button" onClick={() => set(tf.value)}
                          className={`text-xs px-3 py-1.5 border font-section tracking-wider transition-colors ${value===tf.value?"border-white text-white bg-[var(--bg-surface-2)]":"border-[var(--border)] txt-muted hover:border-white/40"}`}>
                          {tf.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Button data-testid="strategy-generate-btn" onClick={generate}
                disabled={generating || !prompt.trim()}
                className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider h-11">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {generating ? "GENERATING…" : "GENERATE STRATEGY"}
              </Button>

              {dsl && (
                <div className="space-y-4 pt-4 border-t border-[var(--border)]">
                  {/* Name + action buttons */}
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="overline">Strategy name</Label>
                      <Input data-testid="strategy-name-input" value={name} onChange={(e) => setName(e.target.value)}
                        className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                    </div>
                    <Button onClick={runPreviewBacktest} disabled={btLoading || !!dslError} variant="outline"
                      className="rounded-none border-white text-white hover:bg-white hover:text-black font-section tracking-wider h-10">
                      {btLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                      {btLoading ? "RUNNING…" : "RUN BACKTEST"}
                    </Button>
                    <Button data-testid="strategy-save-btn" onClick={save}
                      className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider h-10">
                      <Save className="w-4 h-4 mr-2" /> SAVE
                    </Button>
                  </div>

                  {/* Inline backtest result */}
                  {btResult && <BacktestInlineResult result={btResult} />}

                  {/* DSL editor */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="overline">DSL editor</span>
                      {dslError && <span className="text-xs text-red-400 font-mono-data">{dslError}</span>}
                    </div>
                    <textarea
                      data-testid="strategy-dsl-preview"
                      value={dslText}
                      onChange={(e) => onDslTextChange(e.target.value)}
                      spellCheck={false}
                      className={`w-full dsl-viewer bg-[var(--bg-page)] border p-4 text-zinc-200 font-mono text-xs leading-relaxed resize-y min-h-[280px] outline-none focus:ring-1 focus:ring-white/20 ${dslError?"border-red-500/60":"border-[var(--border)]"}`}
                    />
                  </div>

                  {/* Multi-leg structure display */}
                  {dsl?.legs?.length > 0 && (
                    <div className="pt-3 border-t border-[var(--border)]">
                      <LegBuilder dsl={dsl} />
                    </div>
                  )}

                  {/* Scheduler */}
                  <div className="pt-3 border-t border-[var(--border)]">
                    <ScheduleBuilder value={schedule} onChange={setSchedule} />
                  </div>
                </div>
              )}
              </TierGate>
            </div>
          )}

          {/* ── Indicator Builder ── */}
          {builderMode === "indicator" && (
            <div className="p-6">
              <IndicatorStrategyBuilder onSaved={() => { fetchList(); setBuilderMode("library"); }} />
            </div>
          )}

          {/* ── Strategy Library ── */}
          {builderMode === "library" && (
            <div className="p-6">
              <LibraryContent
                items={items}
                stratSearch={stratSearch} setStratSearch={setStratSearch}
                stratFilter={stratFilter} setStratFilter={setStratFilter}
                stratLayout={stratLayout} setStratLayout={setStratLayout}
                onDeploy={toggleDeploy} onLive={openLiveModal}
                onVersions={openVersions} onPerf={openStrategyDrawer}
                onBacktest={runBacktest} onDelete={remove}
                onGate={(s) => setGateModal({ strategyId: s.id, name: s.name, value: s.live_gate?.min_paper_trades ?? 0 })}
                openOnly={openOnly} setOpenOnly={setOpenOnly} openIds={openIds} linkedId={linkedId}
                linkedRow={linkedId ? posRows.find((r) => String(r.strategy_id) === linkedId) || null : null}
                listComplete={listComplete}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Modals / Drawers ── */}
      <GoLiveModal
        state={liveModal} onClose={() => setLiveModal(null)}
        onChange={setLiveModal} onSubmit={submitLiveDeploy}
        submitting={liveSubmitting}
      />


      {/* ── Live Gate Modal ── */}
      {gateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[var(--bg-surface)] border border-[var(--border)] p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-section flex items-center gap-1.5 flex-wrap">
                Live Gate — {gateModal.name}
                <StrategyRefChip id={gateModal.strategyId} testid="strategy-gate-modal-ref" />
                <StrategyStatusChip src={items.find((x) => x.id === gateModal.strategyId)} testid="strategy-gate-modal-status" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setGateModal(null)} className="rounded-none"><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-xs txt-secondary">Set a minimum paper trade count before Live mode can be enabled. Set to 0 to disable gate.</p>
            <div className="flex flex-wrap gap-2">
              {[0, 5, 10, 30, 90].map((n) => (
                <button key={n} type="button"
                  onClick={() => setGateModal(g => ({ ...g, value: n }))}
                  className={`px-4 py-2 border text-xs font-section transition-colors ${gateModal.value === n ? "border-white bg-[var(--bg-surface-2)] text-white" : "border-[var(--border)] txt-muted hover:border-white/40"}`}>
                  {n === 0 ? "OFF" : `${n} trades`}
                </button>
              ))}
            </div>
            <Button onClick={saveGate} className="w-full rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider">
              SAVE GATE
            </Button>
          </div>
        </div>
      )}

      {/* ── Autopilot Candidates Section ── */}
      {autopilotCandidates.length > 0 && (
        <div className="mt-8 space-y-4">
          <div className="flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <Bot className="w-4 h-4 txt-muted" />
            <span className="font-section text-sm">Autopilot Candidates</span>
            <span className="text-xs txt-muted">— AI-built off-hours, gate pre-checked</span>
            <span className="ml-auto text-[10px] font-section border border-amber-500/30 text-amber-400 px-2 py-0.5">ELITE</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {autopilotCandidates.map((c) => (
              <div key={c.id} className="panel p-4 space-y-3 border border-[var(--border)]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-section text-sm truncate">{c.name}</div>
                    <div className="text-xs txt-muted mt-0.5 font-mono-data">{c.symbol} · {c.dsl?.timeframe || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.rank_label && (
                      <span className={`text-[10px] font-section px-1.5 py-0.5 border ${
                        c.rank_label === "S" ? "bg-amber-500/20 text-amber-300 border-amber-500/40" :
                        c.rank_label === "A" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                        "border-[var(--border)] txt-muted"
                      }`}>{c.rank_label}</span>
                    )}
                    {c.live_gate?.gate_passed
                      ? <span className="text-[10px] font-section px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">GATE ✓</span>
                      : <span className="text-[10px] txt-muted border border-[var(--border)] px-1.5 py-0.5 font-section">GATE ✗</span>
                    }
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center border border-[var(--border)] bg-[var(--bg-page)] p-2">
                  {[
                    { label: "TRADES", val: c.auto_paper_results?.trade_count ?? "—" },
                    { label: "WIN %",  val: c.auto_paper_results?.win_rate != null ? `${c.auto_paper_results.win_rate.toFixed(0)}%` : "—" },
                    { label: "PF",     val: c.auto_paper_results?.profit_factor != null ? c.auto_paper_results.profit_factor.toFixed(2) : "—" },
                  ].map(({ label, val }) => (
                    <div key={label}>
                      <div className="text-[10px] txt-muted font-section">{label}</div>
                      <div className="font-mono-data text-sm font-bold">{val}</div>
                    </div>
                  ))}
                </div>
                <Button size="sm"
                  onClick={() => adoptCandidate(c.id, c.name)}
                  disabled={adoptingId === c.id}
                  className="w-full rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider text-xs h-8">
                  {adoptingId === c.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bot className="w-3 h-3 mr-1" />}
                  ADOPT STRATEGY
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {versionsPanel && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50" onClick={() => setVersionsPanel(null)} />
          <div className="w-full max-w-xl bg-[var(--bg-surface)] border-l border-[var(--border)] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
              <div>
                <div className="font-display text-lg">Version history</div>
                <div className="overline txt-secondary flex items-center gap-1.5 flex-wrap">
                  {versionsPanel.name}
                  <StrategyRefChip id={versionsPanel.strategyId} testid="strategy-versions-ref" />
                  <StrategyStatusChip src={items.find((x) => x.id === versionsPanel.strategyId)} testid="strategy-versions-status" />
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setVersionsPanel(null)} className="rounded-none">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {versionsLoading && <div className="txt-muted text-sm">Loading…</div>}
              {!versionsLoading && versionsPanel.versions.length === 0 && (
                <div className="txt-muted text-sm">No archived versions yet.</div>
              )}
              {versionsPanel.versions.map((v) => (
                <div key={v.id} className="border border-[var(--border)] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-section text-sm">v{v.version}</span>
                      <span className="font-mono-data text-xs txt-secondary">{v.saved_at?.slice(0,19)}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => restoreVersion(versionsPanel.strategyId, v)}
                      className="rounded-none h-7 px-3 text-xs font-section hover:bg-[var(--bg-surface-2)]">
                      RESTORE
                    </Button>
                  </div>
                  <pre className="text-[11px] font-mono-data txt-secondary bg-[var(--bg-page)] border border-[var(--border)] p-3 overflow-x-auto max-h-40 whitespace-pre-wrap">
                    {JSON.stringify(v.dsl, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
